import { getRegistry, isKeyhiveDoc, isLoadablePlugin, } from "@inkandswitch/patchwork-plugins";
import { OverlayRepo, registerPatchworkViewTag, } from "@inkandswitch/patchwork-providers";
import { MountedEvent, UnmountedEvent } from "./events.js";
import { LegacyImpl } from "./legacy-impl.js";
import debug from "debug";
const log = debug("patchwork:elements:patchwork-view");
const State = {
    none: "none",
    initializing: "initializing",
    rendering: "rendering",
    unable: "unable",
    rendered: "rendered",
    error: "error",
};
const ATTRS = {
    component: "component",
    url: "url",
    docUrl: "doc-url",
    toolId: "tool-id",
};
export function registerPatchworkViewElement(params) {
    const name = params.name ?? "patchwork-view";
    registerPatchworkViewTag(name);
    if (customElements.get(name))
        return;
    customElements.define(name, class PatchworkViewElement extends HTMLElement {
        #capturedParent = null;
        #component = null;
        #url = null;
        #loaded = null;
        #state = State.none;
        #initEpoch = 0;
        #teardowns = new Set();
        #teardownPromise = null;
        #keyhiveRetrySetup = false;
        #handlingKeyhiveSync = false;
        #pendingKeyhiveSync = false;
        // True when `unable` because access hasn't synced yet.
        // Receiving a keyhive op can recover this.
        #unableNoAccess = false;
        // Realm-local remapping shim, created lazily on first render. Exposed as
        // `element.repo` so tools resolve their primary handle through it.
        #overlayRepo = null;
        repo;
        // In legacy mode, the legacy view logic is hosted on *this*
        // element (not a child) so events dispatched on `<patchwork-view>`
        // and listeners attached by the mounted tool land on the same
        // DOM node — matching the pre-split behavior.
        #legacyImpl = null;
        get component() {
            return this.#component;
        }
        set component(id) {
            if (this.#component === id)
                return;
            this.#component = id;
            const attr = this.getAttribute(ATTRS.component);
            if (attr == id)
                return;
            if (id)
                this.setAttribute(ATTRS.component, id);
            else
                this.removeAttribute(ATTRS.component);
        }
        get url() {
            return this.#url;
        }
        set url(url) {
            if (this.#url === url)
                return;
            this.#url = url;
            const attr = this.getAttribute(ATTRS.url);
            if (attr == url)
                return;
            if (url)
                this.setAttribute(ATTRS.url, url);
            else
                this.removeAttribute(ATTRS.url);
        }
        get docUrl() {
            return this.getAttribute(ATTRS.docUrl);
        }
        set docUrl(url) {
            if (this.docUrl === url)
                return;
            if (url)
                this.setAttribute(ATTRS.docUrl, url);
            else
                this.removeAttribute(ATTRS.docUrl);
        }
        get toolId() {
            return this.getAttribute(ATTRS.toolId);
        }
        set toolId(id) {
            if (this.toolId === id)
                return;
            if (id)
                this.setAttribute(ATTRS.toolId, id);
            else
                this.removeAttribute(ATTRS.toolId);
        }
        static get observedAttributes() {
            return [ATTRS.component, ATTRS.url, ATTRS.docUrl, ATTRS.toolId];
        }
        connectedCallback() {
            this.#capturedParent = this.parentElement;
            this.hive = params.hive;
            this.#component = this.getAttribute(ATTRS.component);
            this.#url = this.getAttribute(ATTRS.url);
            // A same-task remove-and-reinsert (keyed reorder, `insertBefore`)
            // reconnects while the disconnect's teardown is still in flight;
            // syncing now would no-op against the not-yet-reset state and the
            // view would stay blank forever. Wait the teardown out.
            if (this.#teardownPromise) {
                void this.#teardownPromise.then(() => {
                    if (this.isConnected)
                        this.#sync();
                });
            }
            else {
                this.#sync();
            }
        }
        disconnectedCallback() {
            // The overlay repo survives attribute-driven re-syncs; only a real
            // disconnect releases it. Detach now so a re-connect gets a fresh
            // shim, then dispose once the teardown has run.
            const overlayRepo = this.#overlayRepo;
            this.#overlayRepo = null;
            void this.#teardown().then(() => overlayRepo?.dispose());
        }
        connectedMoveCallback() {
            this.#capturedParent = this.parentElement;
            this.#legacyImpl?.connectedMoveCallback();
        }
        attributeChangedCallback(attrName, old, val) {
            if (old === val)
                return;
            if (attrName === ATTRS.component)
                this.#component = val;
            else if (attrName === ATTRS.url)
                this.#url = val;
            // Already in legacy mode and still want it: forward doc-url /
            // tool-id changes into the impl so it tears down and re-inits
            // without us having to rebuild the element.
            if (this.#legacyImpl && !this.#wantsComponent()) {
                if (attrName === ATTRS.docUrl || attrName === ATTRS.toolId) {
                    this.#legacyImpl.attributeChangedCallback(attrName, old, val);
                }
                return;
            }
            // Already mounting a component and still want one: only react
            // to the component-driving attributes; doc-url / tool-id are
            // ignored while a `component` attribute is set.
            if (this.#state !== State.none && this.#wantsComponent()) {
                if (attrName !== ATTRS.component && attrName !== ATTRS.url)
                    return;
            }
            // Either transitioning between forms or initial setup after an
            // attribute landed on an idle element. Tear down whatever is
            // there (no-op when idle) and re-sync against the attributes.
            void this.#teardown().then(() => this.#sync());
        }
        #wantsComponent() {
            return this.hasAttribute(ATTRS.component);
        }
        #wantsLegacy() {
            return (!this.hasAttribute(ATTRS.component) &&
                (this.hasAttribute(ATTRS.docUrl) || this.hasAttribute(ATTRS.toolId)));
        }
        #sync() {
            if (this.#wantsLegacy()) {
                this.#renderLegacy();
                return;
            }
            if (this.#wantsComponent()) {
                // Legacy mode leaves `display` alone so sites' CSS rules on
                // `patchwork-view` (e.g. `display: block`) apply.
                if (!this.style.display)
                    this.style.display = "contents";
                this.#initComponent();
            }
        }
        #renderLegacy() {
            if (this.#legacyImpl)
                return;
            this.#legacyImpl = new LegacyImpl(this, {
                repo: this.#ensureOverlayRepo(),
                hive: params.hive,
            });
            this.#legacyImpl.connectedCallback();
        }
        // The overlay shim is per-element (it dispatches `repo:handle-descriptor`
        // from `this`) but wraps the single shared base repo.
        #ensureOverlayRepo() {
            if (!this.#overlayRepo) {
                this.#overlayRepo = new OverlayRepo(params.repo, this);
                this.repo = this.#overlayRepo;
            }
            return this.repo;
        }
        #initComponent = async () => {
            if (this.#state != State.none)
                return;
            if (!this.component)
                return;
            const registry = getRegistry("patchwork:component");
            const epoch = ++this.#initEpoch;
            this.#state = State.initializing;
            if (params.hive && this.url) {
                // Legacy (padded-zero) doc: skip keyhive gate
                const keyhiveDoc = isKeyhiveDoc(this.url);
                if (keyhiveDoc) {
                    const bestAccess = await params.hive.bestAccessForDoc(params.hive.active.individual.id, this.url);
                    if (epoch !== this.#initEpoch)
                        return;
                    const accessLevel = bestAccess ? bestAccess.toString() : "None";
                    if (accessLevel === "None") {
                        this.#state = State.unable;
                        this.#unableNoAccess = true;
                        if (!this.#keyhiveRetrySetup) {
                            this.#keyhiveRetrySetup = true;
                            const onKeyhiveSync = () => this.#handleKeyhiveSync();
                            params.hive.networkAdapter.on("ingest-remote", onKeyhiveSync);
                            this.#teardowns.add(() => {
                                params.hive.networkAdapter.off("ingest-remote", onKeyhiveSync);
                            });
                        }
                        return;
                    }
                }
                if (!this.#keyhiveRetrySetup) {
                    this.#keyhiveRetrySetup = true;
                    const onKeyhiveSync = () => this.#handleKeyhiveSync();
                    params.hive.networkAdapter.on("ingest-remote", onKeyhiveSync);
                    this.#teardowns.add(() => {
                        params.hive.networkAdapter.off("ingest-remote", onKeyhiveSync);
                    });
                }
                if (keyhiveDoc) {
                    // Access is confirmed, but the doc's content may not have synced
                    // yet.
                    const progress = params.repo.findWithProgress(this.url);
                    const initial = progress.peek();
                    if (initial.state === "failed") {
                        console.error(`patchwork-view: failed to load ${this.url}`, initial.error);
                        this.#state = State.error;
                        return;
                    }
                    if (initial.state !== "ready") {
                        this.#state = State.unable;
                        const unsubscribe = progress.subscribe((queryState) => {
                            if (epoch !== this.#initEpoch)
                                return;
                            if (queryState.state === "ready") {
                                void this.#teardown().then(() => this.#sync());
                            }
                            else if (queryState.state === "failed") {
                                console.error(`patchwork-view: failed to load ${this.url}`, queryState.error);
                                this.#state = State.error;
                            }
                        });
                        this.#teardowns.add(unsubscribe);
                        return;
                    }
                }
            }
            if (epoch !== this.#initEpoch)
                return;
            const removeAddedListener = registry.on("registered", async (added) => {
                if (added.id !== this.component)
                    return;
                if (isLoadablePlugin(added)) {
                    registry.load(added.id);
                }
            });
            const removeLoadedListener = registry.on("loaded", async (loaded) => {
                if (loaded.id !== this.component)
                    return;
                if (this.#state == "unable" || this.#state == "initializing") {
                    this.#queueRender();
                    return;
                }
                // Hot reload: a newer importUrl re-mounts.
                if (this.#state == "error" || this.#state == "rendered") {
                    if (loaded.importUrl !== this.#loaded?.importUrl) {
                        await this.#teardown();
                        this.#sync();
                    }
                }
            });
            this.#teardowns.add(() => {
                removeAddedListener();
                removeLoadedListener();
            });
            if (epoch !== this.#initEpoch)
                return;
            this.#queueRender();
        };
        // Serialized: overlapping calls (both observed attributes changing in
        // one tick, disconnect racing an attribute change) share the one
        // in-flight run instead of double-running the same cleanups.
        #teardown() {
            if (!this.#teardownPromise) {
                this.#teardownPromise = this.#runTeardown().finally(() => {
                    this.#teardownPromise = null;
                });
            }
            return this.#teardownPromise;
        }
        async #runTeardown() {
            if (this.#legacyImpl) {
                const impl = this.#legacyImpl;
                this.#legacyImpl = null;
                await impl.disconnectedCallback();
            }
            if (this.#state != State.none) {
                // Only `rendered` reached the `MountedEvent` dispatch.
                const wasMounted = this.#state == State.rendered;
                const mountedComponentId = this.#loaded?.id;
                this.#initEpoch++;
                const teardowns = [...this.#teardowns];
                this.#teardowns.clear();
                for (const fn of teardowns) {
                    await fn?.();
                }
                this.#loaded = null;
                this.#keyhiveRetrySetup = false;
                this.#unableNoAccess = false;
                this.#state = State.none;
                if (wasMounted && mountedComponentId) {
                    this.#dispatchUnmount(new UnmountedEvent({ componentId: mountedComponentId }));
                }
            }
            this.#overlayRepo?.dispose();
            this.#overlayRepo = null;
        }
        // Bubbling is a no-op when detached; fall back to the closest
        // still-connected ancestor.
        #dispatchUnmount(event) {
            if (this.isConnected) {
                this.dispatchEvent(event);
                return;
            }
            let node = this.#capturedParent;
            while (node && !node.isConnected)
                node = node.parentElement;
            if (node)
                node.dispatchEvent(event);
        }
        async #handleKeyhiveSync() {
            if (!this.url || !params.hive)
                return;
            if (this.#handlingKeyhiveSync) {
                this.#pendingKeyhiveSync = true;
                return;
            }
            this.#handlingKeyhiveSync = true;
            this.#pendingKeyhiveSync = false;
            try {
                let hasAccess = false;
                let accessCheckSucceeded = false;
                try {
                    if (!isKeyhiveDoc(this.url))
                        return;
                    const bestAccess = await params.hive.bestAccessForDoc(params.hive.active.individual.id, this.url);
                    hasAccess = !!bestAccess;
                    accessCheckSucceeded = true;
                }
                catch {
                    return;
                }
                const isDisplayed = this.#state === State.rendered;
                const isUnable = this.#state === State.unable;
                if (hasAccess && isUnable && this.#unableNoAccess) {
                    // Was unable because access hadn't synced, but it just did. Re-sync.
                    await this.#teardown();
                    this.#sync();
                }
                else if (!hasAccess && isDisplayed && accessCheckSucceeded) {
                    await this.#teardown();
                    this.#sync();
                }
            }
            finally {
                this.#handlingKeyhiveSync = false;
                if (this.#pendingKeyhiveSync) {
                    this.#handleKeyhiveSync();
                }
            }
        }
        #queueRender() {
            if (this.#state == "none")
                return;
            if (this.#state == "rendering")
                return;
            this.#state = "rendering";
            queueMicrotask(() => this.#renderComponent());
        }
        #renderComponent() {
            if (this.#state != "rendering")
                return;
            if (!this.component) {
                this.#state = "unable";
                return;
            }
            const componentId = this.component;
            const registry = getRegistry("patchwork:component");
            this.#loaded = registry.get(componentId) ?? null;
            if (!this.#loaded) {
                this.#state = "unable";
                console.warn(`patchwork-view: no component registered with id "${componentId}"`);
                return;
            }
            if (!this.#loaded.module) {
                registry.load(this.#loaded.id);
                if (registry.isLoading(this.#loaded.id)) {
                    this.#state = "unable";
                    log(`loading component "${componentId}"`);
                }
                else {
                    this.#state = "unable";
                    console.warn(`patchwork-view: failed to load component "${componentId}"`);
                }
                return;
            }
            try {
                // Expose `element.repo` (the overlay shim) so components that want
                // ancestor-remapped resolution can opt in; the render fn still
                // receives the base repo (see ComponentRender docs).
                this.#ensureOverlayRepo();
                const cleanup = this.#loaded.module(this, params.repo);
                if (typeof cleanup === "function") {
                    this.#teardowns.add(cleanup);
                }
                else {
                    console.warn(`return a cleanup function from ${componentId}`);
                }
                this.#state = "rendered";
                this.dispatchEvent(new MountedEvent({ componentId }));
            }
            catch (error) {
                console.error(`patchwork-view: component "${componentId}" threw during mount`, error);
                this.#state = "error";
            }
        }
    });
}
